import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Popper,
  TextField,
  Checkbox,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState, useCallback } from 'react';

import CheckboxIcon from '../../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: rowAutocomlpete
const StyledRowAutocomlpete = styled(Autocomplete, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '0',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
      height: 'auto',
      ...(changedStyle && {
        color: '#212346',
      }),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
  },
  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));

const ArtEditCellSelect = ({
  field,
  rowId,
  accessToken,
  socket,
  cellProps,
  handleField,
  getOptionLabel,
}) => {
  const getLocalString = useTranslation();
  const [options, setOptions] = useState([]);
  const [isMultiple, setIsMultiple] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setOptions([]);
    if (socket && field && rowId && accessToken) {
      socket.emit('getArtFieldDefinition', {
        fieldId: field,
        rowId,
        token: accessToken,
      });
      const handler = data => {
        if (isMounted && data?.values) {
          setOptions(data.values);
          setIsMultiple(data.isMultiple);
          setLoading(false);
        }
      };
      socket.once('artFieldDefinition', handler);
      return () => {
        isMounted = false;
        socket.off('artFieldDefinition', handler);
      };
    } else {
      setLoading(false);
    }
  }, [socket, field, rowId, accessToken, cellProps]);

  const handleChange = useCallback(
    (_, newValue) => {
      if (isMultiple) {
        handleField(
          { target: { value: newValue ? newValue.map(v => v.valueId) : [] } },
          cellProps,
        );
      } else {
        handleField(
          { target: { value: newValue ? newValue.valueId : null } },
          cellProps,
        );
      }
      if (cellProps?.api && cellProps.api.stopCellEditMode) {
        cellProps.api.stopCellEditMode({
          id: cellProps.id,
          field: cellProps.field,
        });
      }
    },
    [handleField, cellProps, isMultiple],
  );

  function getValueFromOptions(options, value) {
    if (!value && value !== 0) {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const strValue = String(value);
      const foundByValueId = options.find(
        opt => String(opt.valueId) === strValue,
      );
      if (foundByValueId) {
        return foundByValueId;
      }
      const foundById = options.find(opt => String(opt.id) === strValue);
      if (foundById) {
        return foundById;
      }
      const foundByPrimitive = options.find(opt => opt === value);
      if (foundByPrimitive) {
        return foundByPrimitive;
      }
      return null;
    }
    if (typeof value === 'object') {
      if (value.valueId) {
        return options.find(opt => opt.valueId === value.valueId) || null;
      }
      if (value.id) {
        return options.find(opt => opt.id === value.id) || null;
      }
    }
    return null;
  }

  const safeGetOptionLabel = option => {
    if (!option) {
      return '';
    }
    if (getOptionLabel) {
      return String(getOptionLabel(option));
    }
    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }
    return (
      option.valueName ||
      option.label ||
      option.name ||
      String(option.valueId || option.id || '')
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <StyledRowAutocomlpete
      changedStyle={cellProps.row.changed}
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      noOptionsText={getLocalString('labels', 'notFound')}
      onChange={handleChange}
      disablePortal
      options={options || []}
      disableClearable={!isMultiple}
      renderOption={(props, option, { selected }) => (
        <MenuItem
          {...props}
          key={option.valueId || option.id || option.value || option}
          value={option.valueId || option.id || option.value || option}
          selected={cellProps.value === option.valueName}
        >
          {isMultiple && (
            <CheckboxIconChecked
              style={{
                visibility: selected ? 'visible' : 'hidden',
                marginRight: 8,
              }}
              width='12'
              height='12'
              viewBox='0 0 12 12'
            />
          )}
          {safeGetOptionLabel(option)}
        </MenuItem>
      )}
      value={
        isMultiple
          ? Array.isArray(cellProps.value)
            ? options.filter(opt => cellProps.value.includes(opt.valueId))
            : []
          : getValueFromOptions(options, cellProps.value)
      }
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) {
          return false;
        }
        if (option === value) {
          return true;
        }
        return String(option.valueId) === String(value.valueId);
      }}
      getOptionLabel={safeGetOptionLabel}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={
            cellProps.value
              ? cellProps.value
              : getLocalString('labels', 'select')
          }
          autoFocus
        />
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={getLocalString('labels', 'select')}
          autoFocus
        />
      )}
      renderTags={
        isMultiple
          ? (value, getTagProps) => [
              <span
                key='selected-names'
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {value.map(opt => safeGetOptionLabel(opt)).join(', ')}
              </span>,
            ]
          : undefined
      }
      popupIcon={<ChevronIcon direction={'down'} />}
      fullWidth
      autoFocus
      slots={{
        popper: props => (
          <Popper
            {...props}
            sx={{ width: '200px!important' }}
            placement='bottom-start'
          ></Popper>
        ),
      }}
    />
  );
};

export default ArtEditCellSelect;
